import React from 'react'
import { isEmpty } from 'lodash'
import {
  FIELD_TYPE,
  METRIC,
  CHART_INTERVAL,
  CHART_THRESHOLD_TYPE,
  CHART_DATA_SOURCE,
  CHART_INSIGHT_MODE,
  KEY_FINDING_INCLUDE,
  KEY_FINDING_TEXT_SIZE,
  BRIEFING_SUMMARY_INCLUDE,
  BRIEFING_ANOMALY_RADAR_CHANGE_SENSITIVITY,
  BRIEFING_ANOMALY_RADAR_EXCLUDE,
  BRIEFING_ANOMALY_RADAR_FORMAT,
  BRIEFING_ANOMALY_RADAR_INCLUDE,
  BRIEFING_CHANGE_DRIVERS_CHANGE_SENSITIVITY,
  BRIEFING_CHANGE_DRIVERS_FOCUS_ON,
  BRIEFING_CHANGE_DRIVERS_FORMAT,
  BRIEFING_CHANGE_DRIVERS_INCLUDE,
  BRIEFING_PATTERN_FINDER_CHANGE_SENSITIVITY,
  BRIEFING_PATTERN_FINDER_FORMAT,
  BRIEFING_PATTERN_FINDER_INCLUDE,
  BRIEFING_PROPOSE_ACTIONS_FORMAT,
  BRIEFING_PROPOSE_ACTIONS_TYPE,
  BRIEFING_SUMMARY_FORMAT,
  BRIEFING_TIMELINE_OF_EVENTS_FORMAT,
  BRIEFING_TIMELINE_OF_EVENTS_INCLUDE,
  BRIEFING_PROPOSE_ACTIONS_ACTION_TYPE
} from '../chartConfigurationsV2'
import {
  ChartIntervalInput,
  TextColorInput,
  ThresholdInput,
  MetricInput,
  MetricInputByCategory,
  DeviceInput,
  SingleColorSelector,
} from './inputs/CustomChartInputs'
import { DataSource } from './inputs/DataSource'
import { ChartTypeSelect } from './inputs/ChartTypeSelect'
import { TextInput } from './inputs/TextInput'
import { CardType } from './inputs/CardType'
import { Select } from './inputs/Select'
import { MultiSelect } from './inputs/MultiSelect'
import { Switch } from './inputs/Switch'
import { DataStreams } from './inputs/DataStreams'
import { SingleDataStreams } from './inputs/SingleDataStreams'
import { DataStreamCard } from './inputs/DataStreamCard'
import { NumberInput } from './inputs/NumberInput'
import { ColorTiers } from './inputs/ColorTiers'
import { TextAreaInput } from './inputs/TextAreaInput'
import { BriefingData } from './inputs/BriefingData'
import { KeyFinding } from './inputs/KeyFinding'
import { DASHBOARD_ACCENT_COLOR } from '../../../../../utils/constants'
import { VuiFormGroup, VuiForm } from '@topkamera/viso-ui'
import { stringArrayToOptions, stringArrayToOptionsRaw, findOptions } from 'utils/common'
import { EnumType } from 'json-to-graphql-query'

export const renderField = (fieldId, fields, hiddenFields = [], values = {}, props = {}) => {
  const isHiddenField = hiddenFields.includes(fieldId)

  if (isHiddenField) return <div>test</div>

  const field = fields[fieldId]

  const value = values?.[fieldId] ?? field?.default ?? ''

  if (!field) return null

  const getSelectedOption = (options, val) => {
    // Treat 0 as a valid value; check for undefined/null instead of falsy
    if ((val !== undefined && val !== null) && !isEmpty(options)) {
      const selectedOption = options.find(opt => opt.value === val)
      if (selectedOption) return selectedOption
    }
    return {}
  }

  const getValueFromSelectedOption = (selectedOption) => {
    // Return value even if it's 0; only treat undefined/null as empty
    if (!isEmpty(selectedOption) && ('value' in selectedOption)) {
      return selectedOption.value
    }
    return null
  }

  const getMetricSelectedOptions = (val) => {
    if (val && !isEmpty(val)) {
      const selectedOptions = METRIC.OPTION_TRANSFORMER(val.metrics, val.selectedTableName)

      return selectedOptions
    }
  }

  const getValuesFromMetricOptions = (selectedMetricOptions, metricsConfig, val) => {
    const newValue = { ...val }

    if (selectedMetricOptions.length === 0) {
      newValue.selectedTableName = ''
      newValue.metrics = []
    } else {
      newValue.metrics = selectedMetricOptions.map(opt => {
        newValue.selectedTableName = opt.tableName
        return opt.value
      })
    }
    newValue.metricConfig = { ...metricsConfig }
    return newValue
  }

  const getMultiSelectSelectedOption = (val) => {
    return val.map(opt => ({
      value: opt,
      label: opt
    }))
  }

  // const getDevicesMultiSelectSelectedOption = (val) => {
  //   // const deviceobj = {
  //   //   _id: '',
  //   //   name: '',
  //   //   archived: false
  //   // }
  //   return val.map(device => device.name)
  // }

  // const getDevicesValuesFromMetricOptions = (option, devices) => {
  // }

  switch (field.type) {
    case FIELD_TYPE.CHART_TYPE:
      return (
        <ChartTypeSelect
          className='chart-editor-type'
          color={props?.color}
          key={field.id}
          label={field.label}
          workspace={props?.workspace}
          placeholder={field.placeholder}
          selectedOption={getSelectedOption(field.options, value)}
          options={field.options || []}
          onChange={selectedOption => {
            const val = getValueFromSelectedOption(selectedOption)
            props?.onChangeField(fieldId, val)
          }}
        />
      )
    case FIELD_TYPE.TEXT:
      let error = ''
      if (fieldId === 'chartTitle') { }

      return (
        <TextInput
          color={props?.color}
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          hasBorder={field.hasBorder}
          value={value}
          // errorMessage={'Chart title already in use.'}
          // error={!!error}
          onChange={val => props?.onChangeField(fieldId, val)}
        />
      )
    case FIELD_TYPE.SELECT:
      // Dynamically restrict threshold options for Gauge and Card charts
      // to hide the 'percent' threshold type, using CHART_THRESHOLD_TYPE.SINGLE_OPTIONS
      {
        let effectiveOptions = field.options
        if (
          (fieldId === 'thresholdType' || fieldId === 'allThresholdType') &&
          (values?.chartType === 'gauge' || values?.chartType === 'card')
        ) {
          effectiveOptions = CHART_THRESHOLD_TYPE.SINGLE_OPTIONS
        }

        // Handle dynamic options based on form data
        if (field.getDynamicOptions && typeof field.getDynamicOptions === 'function') {
          effectiveOptions = field.getDynamicOptions(values)
        }

        // Handle default value for dynamic fields
        let effectiveValue = value !== undefined && value !== null ? value : field?.selectedOption?.value;

        if (field.getDefaultValue && typeof field.getDefaultValue === 'function') {
          const defaultValue = field.getDefaultValue(values);
          // If current value is not in the new options, use the default value
          const currentValueExists = effectiveOptions.some(opt => opt.value === effectiveValue);
          if (!currentValueExists) {
            effectiveValue = defaultValue;
            // Trigger onChange to update the form with the new default value
            setTimeout(() => {
              props?.onChangeField(fieldId, defaultValue);
            }, 0);
          }
        }

        const selected = getSelectedOption(effectiveOptions, effectiveValue);

        return (
          <Select
            key={field.id}
            label={field.label}
            hasBorder={field.hasBorder}
            placeholder={field.placeholder}
            isSearchable={field.isSearchable}
            selectedOption={selected}
            options={effectiveOptions}
            onChange={selectedOption => {
              const val = getValueFromSelectedOption(selectedOption)
              props?.onChangeField(fieldId, val)
            }}
          />
        )
      }
    case FIELD_TYPE.CARD_TYPE:
      return (
        <CardType
          field={field}
          value={value}
          streams={values}
          onChange={val => {
            props?.onChangeField(fieldId, val)
          }} workspace={props?.workspace} />
      )
    case FIELD_TYPE.TOGGLE_BUTTON:
      return (
        <Switch
          key={field.id}
          label={field.label}
          checked={value}
          onChange={val => props?.onChangeField(fieldId, val)}
        />
      )
    case FIELD_TYPE.DATA_STREAMS:
      // Hide analysis mode when table chart is in raw data mode
      const hideAnalysisMode = values?.chartType === 'table' && values?.tableColumnMetricData === 'raw'

      return (
        <DataStreams
          key={field.id}
          value={value.value}
          streams={value.streams}
          chartValues={values}
          allChartValues={props.chartValues}
          // metricsConfig={value?.metricConfig}
          // selectedMetrics={getMetricSelectedOptions(props.selectedMetrics)}
          metricData={props.metricData}
          selectedTableName={props.selectedTableName}
          onChange={newDataStreams => {
            props?.onChangeField(fieldId, newDataStreams)
          }}
          workspace={props?.workspace}
          maxDataStreams={field.maxDataStreams}
          hideAnalysisMode={hideAnalysisMode}
          color={props?.color}
        />
      )
    case FIELD_TYPE.DATA_STREAMS_SINGLE:
      // Hide analysis mode when table chart is in raw data mode
      return (
        <SingleDataStreams
          key={field.id}
          value={values.dataStreamSingle}
          streams={values.dataStreamSingle}
          chartValues={values}
          getMetricSelectedOptions={getMetricSelectedOptions}
          // metricsConfig={value?.metricConfig}
          // selectedMetrics={getMetricSelectedOptions(props.selectedMetrics)}
          metricData={props.metricData}
          selectedTableName={props.selectedTableName}
          onChange={newDataStreams => {
            props?.onChangeField(fieldId, newDataStreams)
          }}
          workspace={props?.workspace}
          maxDataStreams={field.maxDataStreams}
          hideAnalysisMode={hideAnalysisMode}
          color={props?.color}
        />
      )
    case FIELD_TYPE.DATA_STREAMS_CARD:
      // Hide analysis mode when table chart is in raw data mode
      return (
        <DataStreamCard
          color={props?.color}
          key={field.id}
          value={values.dataStreamCard}
          streams={values.dataStreamCard}
          chartValues={values}
          // metricsConfig={value?.metricConfig}
          // selectedMetrics={getMetricSelectedOptions(props.selectedMetrics)}
          metricData={props.metricData}
          selectedTableName={props.selectedTableName}
          onChange={newDataStreams => {
            props?.onChangeField('dataStreamCard', newDataStreams)
          }}
          workspace={props?.workspace}
          maxDataStreams={field.maxDataStreams}
          hideAnalysisMode={hideAnalysisMode}
        />
      )
    case FIELD_TYPE.MULTI_SELECT:
      return (
        <MultiSelect
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          selectedOptions={getMultiSelectSelectedOption(field.options, value)}
          options={field.options}
          onChange={selectedOptions => {
            const val = getValueFromSelectedOption(selectedOptions)
            props?.onChangeField(fieldId, val)
          }}
        />
      )
    case FIELD_TYPE.DATE:
      return (
        <Select
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          selectedOption={value}
          options={field.options}
          withTimeRangeMenu={true}
          timeRangeOverflow='close'
          timeRangeAlignment='start'
          timeRangePosition='top'
          onChange={selectedOption => {
            props?.onChangeField(fieldId, selectedOption)
            // const val = getValueFromSelectedOption(selectedOption)
          }}
        />
      )
    case FIELD_TYPE.ANALYSIS_INTERVAL:
      return (
        <ChartIntervalInput
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          intervalQuantity={value?.quantity || 1}
          selectedIntervalOption={getSelectedOption(CHART_INTERVAL.OPTIONS, value.value)}
          intervalOptions={CHART_INTERVAL.OPTIONS}
          onChange={(intervalKey, intervalValue) => {
            const newValue = { ...value }
            newValue[intervalKey] = intervalValue
            props?.onChangeField(fieldId, newValue)
          }}
        />
      )
    case FIELD_TYPE.METRIC:
      return (
        <MetricInput
          key={field.id}
          id={field.id}
          label={field.label}
          showSubConfig={field.showSubConfig}
          placeholder={field.placeholder}
          selectedTableName={props.selectedTableName}
          metricsConfig={value?.metricConfig}
          selectedMetrics={getMetricSelectedOptions(value)}
          metricOptions={props.metricData}
          onChangeSelectedMetrics={(selectedOptions, metricConfigs) => {
            const val = getValuesFromMetricOptions(selectedOptions, metricConfigs, value)
            props?.onChangeField(fieldId, val)
          }}
          onChangeMetricConfig={(metric, metricConfig) => {
            const newValue = { ...value }
            newValue.metricConfig = { ...metricConfig }
            props?.onChangeField(fieldId, newValue)
          }}
          chartType={props?.chartValues?.chartType}
          chartValues={{ ...(props?.chartValues || {}), ...(values || {}) }}
          workspace={props?.workspace}
        />
      )

    case FIELD_TYPE.SINGLE_COLOR_SELECTOR:
      return (
        <SingleColorSelector
          key={field.id}
          id={field.id}
          label={field.label}
          value={value}
          placeholder={field.placeholder}
          onChangeColorSelector={(val) => {
            props?.onChangeField(fieldId, val)
          }}
          workspace={props?.workspace}
        />
      )
    case FIELD_TYPE.METRIC_WITH_GROUP:
      return (
        <MetricInputByCategory
          key={field.id}
          id={field.id}
          label={field.label}
          showSubConfig={field.showSubConfig}
          placeholder={field.placeholder}
          selectedTableName={value?.selectedTableName}
          metricsConfig={value?.metricConfig}
          selectedMetrics={getMetricSelectedOptions(props.chartValues.metric)}
          selectedGroup={getMetricSelectedOptions(props.chartValues.groupBy)}
          metricOptions={props.metricData}
          onChangeSelectedMetrics={(selectedOptions, metricConfigs, isGrouped) => {
            const val = getValuesFromMetricOptions(selectedOptions, metricConfigs, value)
            props?.onChangeField(fieldId, val, isGrouped)
          }}
          onChangeMetricConfig={(metric, metricConfig) => {
            const newValue = { ...value }
            newValue.metricConfig = { ...metricConfig }
            props?.onChangeField(fieldId, newValue, isGrouped)
          }}
          chartType={props?.chartValues?.chartType}
          workspace={props?.workspace}
        />
      )
    case FIELD_TYPE.DEVICES:
      return (
        <MultiSelect
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          selectedOptions={value}
          options={props?.devices?.map(device => device.name) || []}
          onChange={val => {
            const newValue = val.map(opt => opt)
            props?.onChangeField(fieldId, newValue)
          }}
          withSimpleMenu={true}
          showSelectedOptions={true}
          withDashboardDefaultOption={true}
        />
      )
    case FIELD_TYPE.COLOR_TIERS:
      return (
        <ColorTiers
          color={props?.color}
          field={field}
          value={value}
          streams={values}
          chartType={props?.chartValues?.chartType}
          onChange={val => {
            props?.onChangeField(fieldId, val)
          }} workspace={props?.workspace} />
      )
    case FIELD_TYPE.NUMBER:
      return (
        <NumberInput
          key={field.id}
          label={field.label}
          min={field.min}
          max={field.max}
          placeholder={field.placeholder}
          value={value}
          onChange={val => props?.onChangeField(fieldId, val)}
        />
      )
    case FIELD_TYPE.TEXT_COLOR:
      // Disable only the color picker for stream labels when thresholds are enabled
      // (for non-table charts). Leave text input enabled. For other TEXT_COLOR fields
      // (e.g., thresholdConfig), keep everything enabled.
      //
      // For *table* charts, tables do not have a base series color, so the streamLabel
      // color blob + picker should always be hidden regardless of thresholds.
      let isThresholdOn = (() => {
        if (values?.chartType === 'table') {
          const tiersCount = values?.colorTiers?.customConfigs?.length || 0
          return tiersCount > 0
        }
        if (values && typeof values.enableThreshold !== 'undefined') {
          return values.enableThreshold
        }
        return !!props?.chartValues?.enableThreshold
      })()
      // For Pie and Doughnut charts, thresholds are not supported, so never disable color picker
      if (values?.chartType === 'pie' || values?.chartType === 'doughnut') {
        isThresholdOn = false
      }

      const hasMultipleGroupBy = props?.chartValues?.groupBy?.metrics?.length > 1
      const isStreamLabel = fieldId === 'streamLabel'
      const isTableChart = values?.chartType === 'table'

      // Only disable stream label color for:
      // - Table charts (no base color concept)
      // - Card charts when threshold is enabled
      // - Multiple groupBy scenarios
      const isCardThresholdOn = values?.chartType === 'card' && !!isThresholdOn
      const colorDisabled = isStreamLabel ? (isTableChart || isCardThresholdOn || hasMultipleGroupBy) : false
      const textDisabled = isStreamLabel && hasMultipleGroupBy
      const hideColorPickerForStreamLabel = isStreamLabel && (
        isTableChart || hasMultipleGroupBy
      )

      if (textDisabled) {
        return <span />
      }

      return (
        <TextColorInput
          key={field.id}
          label={field.label}
          value={value.value}
          chartValues={values}
          hasBorder={field.hasBorder}
          color={value.color}
          theme={props?.color}
          disabled={textDisabled}
          colorDisabled={colorDisabled}
          hideColorPicker={hideColorPickerForStreamLabel}
          onChange={val => props?.onChangeField(fieldId, val)}
        />
      )
    case FIELD_TYPE.TEXT_AREA:
      return (
        <TextAreaInput
          key={field.id}
          label={field.label}
          value={value.value}
          color={value.color}
          onChange={val => props?.onChangeField(fieldId, val)}
        />
      )
    case FIELD_TYPE.DATA_SOURCE:
      return (
        <DataSource
          charts={props?.charts || []}
          key={field.id}
          label={field.label}
          color={DASHBOARD_ACCENT_COLOR}
          values={props?.chartInsightsData}
          options={field.options}
          onChange={val => props?.onChangeInsightsChart(fieldId, val)}
        />
      )
    case FIELD_TYPE.BRIEFING_DATA:
      return (
        <BriefingData
          values={props?.chartInsightsData}
          color={DASHBOARD_ACCENT_COLOR}
          onChange={val => props?.onChangeInsightsChart(fieldId, val)}
        />
      )
    default:
      return <div>No field type defined for {fieldId}</div>
  }
}

export const findMetricTime = (data, selectedTableName) => {
  return data.find(group => group.tableName === selectedTableName)
    ?.metrics.find(metric => metric.name === 'time')
    ?.name
}

export const updateFieldById = (list, id, newValue) => {
  return list.map(item => {
    if (item.id === id) {
      return { ...item, value: newValue }
    }
    return item
  })
}

export const transformBucketedTimeToTime = (chartConfiguration) => {
  // For all chart types (including bar), convert bucketed_time to time for the editor
  // The editor should always show 'time', not 'bucketed_time'

  // If no bucketed_time in groupBy, return as-is
  if (!chartConfiguration?.groupBy?.metrics?.includes('bucketed_time') ||
    !chartConfiguration?.groupBy?.metricConfig?.bucketed_time) {
    return chartConfiguration
  }

  const { bucketed_time, ...restMetricConfig } = chartConfiguration.groupBy.metricConfig

  return {
    ...chartConfiguration,
    groupBy: {
      ...chartConfiguration.groupBy,
      metrics: chartConfiguration.groupBy.metrics.map(metric =>
        metric === 'bucketed_time' ? 'time' : metric
      ),
      metricConfig: {
        ...restMetricConfig,
        time: {
          value: 'time'
        }
      }
    }
  }
}

export const dashboardForm = ({ formProps, fields, onFormChange, description, formRef }) => {
  return (
    <div>
      <div className='vui-text-label'>{description}</div>
      <div className='vui-pt-10'>
        <VuiForm
          {...formProps}
          onChange={onFormChange}
          fields={fields}
          ref={formRef}
        />
      </div>
    </div>
  )
}

export const generateSummaryFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle } = data?.briefing?.summary || {};

  const includeValues = stringArrayToOptions(include) || [];
  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            color,
            minRows: 3,
            placeholder: 'Tune the output of the summary',
            value: instructions
          }
        },
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: BRIEFING_SUMMARY_INCLUDE.OPTIONS,
            value: includeValues,
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_SUMMARY_FORMAT.OPTIONS,
            value: format
          }
        }
      ]
    }
  ]
}

export const generateChangeDriversFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle, changeSensitivity, focusOn } = data?.briefing?.changeDrivers || {}

  const includeValues = stringArrayToOptions(include) || [];

  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            minRows: 3,
            placeholder: '',
            value: instructions
          }
        },
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: BRIEFING_CHANGE_DRIVERS_INCLUDE.OPTIONS,
            value: includeValues
          }
        },
        {
          id: 'focusOn',
          type: 'selectinput',
          groupProps: {
            label: 'Focus on'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_CHANGE_DRIVERS_FOCUS_ON.OPTIONS,
            value: focusOn
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_CHANGE_DRIVERS_FORMAT.OPTIONS,
            value: format
          }
        },
        {
          id: 'changeSensitivity',
          type: 'selectinput',
          groupProps: {
            label: 'Change sensitivity'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_CHANGE_DRIVERS_CHANGE_SENSITIVITY.OPTIONS,
            value: changeSensitivity
          }
        },
      ]
    }
  ]
}

export const generateAnomalyRadarFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle, changeSensitivity, exclude } = data?.briefing?.anomalyRadar || {}
  
  const includeValues = stringArrayToOptions(include) || [];
  const excludeValue = stringArrayToOptions(exclude) || [];

  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            color,
            minRows: 3,
            placeholder: '',
            value: instructions
          }
        },
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: BRIEFING_ANOMALY_RADAR_INCLUDE.OPTIONS,
            value: includeValues
          }
        },
        {
          id: 'exclude',
          type: 'selectinput',
          groupProps: {
            label: 'Exclude'
          },
          fieldProps: {
            isMulti: true,
            color,
            elementId: 'popoverRoot',
            options: BRIEFING_ANOMALY_RADAR_EXCLUDE.OPTIONS,
            value: excludeValue
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_ANOMALY_RADAR_FORMAT.OPTIONS,
            value: format
          }
        },
        {
          id: 'changeSensitivity',
          type: 'selectinput',
          groupProps: {
            label: 'Change sensitivity'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_ANOMALY_RADAR_CHANGE_SENSITIVITY.OPTIONS,
            value: changeSensitivity
          }
        },
      ]
    }
  ]
}

export const generatePatternFinderFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle, changeSensitivity } = data?.briefing?.patternFinder || {}
  const includeValues = stringArrayToOptions(include) || [];

  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            color,
            minRows: 3,
            placeholder: 'Tune the output of the summary',
            value: instructions
          }
        },
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            isMulti: true,
            color,
            elementId: 'popoverRoot',
            options: BRIEFING_PATTERN_FINDER_INCLUDE.OPTIONS,
            value: includeValues
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_PATTERN_FINDER_FORMAT.OPTIONS,
            value: format
          }
        },
        {
          id: 'changeSensitivity',
          type: 'selectinput',
          groupProps: {
            label: 'Change sensitivity'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_PATTERN_FINDER_CHANGE_SENSITIVITY.OPTIONS,
            value: changeSensitivity
          }
        },
      ]
    }
  ]
}

export const generateTimelineOfEventsFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle, changeSensitivity } = data?.briefing?.timelineOfEvents || {}
  const includeValues = stringArrayToOptions(include) || [];

  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            color,
            minRows: 3,
            placeholder: '',
            value: instructions
          }
        },
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: BRIEFING_TIMELINE_OF_EVENTS_INCLUDE.OPTIONS,
            value: includeValues
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            value: format,
            options: BRIEFING_TIMELINE_OF_EVENTS_FORMAT.OPTIONS
          }
        }
      ]
    }
  ]
}

export const generateProposeActionsFields = ({ data, color }) => {
  const { instructions = '', include = [], format, subtitle, changeSensitivity, exclude, actionType } = data?.briefing?.proposeActions || {}
  const includeValues = stringArrayToOptions(actionType) || [];
  const excludeValue = stringArrayToOptions(exclude) || [];

  return [
    {
      id: 'subtitle',
      type: 'textinput',
      groupProps: {
        label: 'Subtitle'
      },
      fieldProps: {
        placeholder: '(Optional)',
        value: subtitle
      },
      fields: [
        {
          id: 'instructions',
          type: 'textarea',
          groupProps: {
            label: 'Instruction'
          },
          fieldProps: {
            color,
            minRows: 3,
            placeholder: '',
            value: instructions
          }
        },
        {
          id: 'actionType',
          type: 'selectinput',
          groupProps: {
            label: 'Action type'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: BRIEFING_PROPOSE_ACTIONS_TYPE.OPTIONS,
            value: includeValues
          }
        },
        {
          id: 'format',
          type: 'selectinput',
          groupProps: {
            label: 'Format'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: BRIEFING_PROPOSE_ACTIONS_FORMAT.OPTIONS,
            value: format
          }
        }
      ]
    }
  ]
}

export const generateTextAreaFields = ({ value, id }) => {
  return [
    {
      id: id,
      type: 'textarea',
      groupProps: {
        label: 'Instruction'
      },
      fieldProps: {
        minRows: 3,
        placeholder: '',
        value: value
      }
    }
  ]
}

export const generateSelectInputFields = ({ id, value }) => {

  return [
    {
      id: id,
      type: 'selectinput',
      groupProps: {
        label: 'Include'
      },
      fieldProps: {
        isMulti: true,
        elementId: 'popoverRoot',
        options: [
          {
            label: '3 action bullets',
            value: '3ActionBullets'
          },
          {
            label: 'Checklist',
            value: 'checklist'
          },
        ],
        value: stringArrayToOptions(value)
      }
    }
  ]
}

export const generateDataSourceFields = ({ charts, data, color }) => {
  const { instructions = '', include = [], dataSource, mode, chartsToInclude } = data

  const chartData = charts.map(item => ({
    label: item.title,
    value: item.id
  }))
  const selectedValues = findOptions(chartData, chartsToInclude);
  const nestedFields = [];

  if (dataSource && !!dataSource.length && dataSource?.find(item => item.value === 'charts')) {
    nestedFields.push({
      hasBorderLeft: true,
      id: 'chartsToInclude',
      type: 'selectinput',
      groupProps: {
        label: 'Charts to include'
      },
      fieldProps: {
        color,
        isMulti: true,
        elementId: 'popoverRoot',
        options: chartData,
        value: selectedValues
      }
    })
  }

  nestedFields.push(
    {
      id: 'info',
      type: 'info',
      fieldProps: {
        level: 'Note:',
        description: 'Choose which charts the AI can reference. Using the full dashboard adds context; selecting specific charts keeps the insight focused.'
      }
    },
    {
      id: 'mode',
      type: 'selectinput',
      groupProps: {
        label: 'Mode'
      },
      fieldProps: {
        color,
        isMulti: false,
        placeholder: 'Briefing/Key Finding',
        elementId: 'popoverRoot',
        options: CHART_INSIGHT_MODE.OPTIONS,
        value: mode
      }
    }
  )


  return [
    {
      id: 'dataSource',
      type: 'selectinput',
      groupProps: {
        label: 'Data Source'
      },
      fieldProps: {
        color,
        isMulti: false,
        placeholder: 'Entire dashboard/Specific chart',
        elementId: 'popoverRoot',
        options: CHART_DATA_SOURCE.OPTIONS,
        value: dataSource
      },
      fields: nestedFields
    }
  ]
}

export const generateKeyFindingFields = ({ data, color }) => {
  const { instructions = '', include, textSize, highlightKeyItems } = data?.briefing?.keyFinding || {}

  return [
    {
      id: 'instructions',
      type: 'textarea',
      groupProps: {
        label: 'Instruction'
      },
      fieldProps: {
        minRows: 3,
        placeholder: '',
        value: instructions
      },
      fields: [
        {
          id: 'include',
          type: 'selectinput',
          groupProps: {
            label: 'Include'
          },
          fieldProps: {
            color,
            isMulti: true,
            elementId: 'popoverRoot',
            options: KEY_FINDING_INCLUDE.OPTIONS,
            value: stringArrayToOptions(include)
          }
        },
        {
          id: 'textSize',
          type: 'selectinput',
          groupProps: {
            label: 'Text size'
          },
          fieldProps: {
            color,
            isMulti: false,
            elementId: 'popoverRoot',
            options: KEY_FINDING_TEXT_SIZE.OPTIONS,
            value: textSize
          }
        },
        {
          id: 'highlightKeyItems',
          type: 'toggleinput',
          groupProps: {
            label: 'Highlight key items',
          },
          fieldProps: {
            value: highlightKeyItems ? [true] : [],
            color,
            items: [
              { id: 'keyItem', value: true, label: '' },
            ]
          }
        },
      ]
    }
  ]
}

export const generateBriefingDataFields = ({ data, color }) => {
  const { title = '', highlightKeyItems } = data?.briefing || {};

  return [
    {
      id: 'title',
      type: 'textinput',
      groupProps: {
        label: 'Title'
      },
      fieldProps: {
        minRows: 3,
        placeholder: 'e.g. Weekly Summary',
        value: title || ''
      },
      fields: [
        {
          id: 'highlightKeyItems',
          type: 'toggleinput',
          groupProps: {
            label: 'Highlight key items',
          },
          fieldProps: {
            color,
            value: [highlightKeyItems],
            items: [
              { id: 'highlightKeyItems', value: true, label: '' },
            ]
          }
        },
      ]
    }
  ]
}

export const aiSightsSwitch = ({ row, values, setValues, setExpandedLabel, validateForm, onRowClick, color }) => {
  const getId = row.modeId
  const getEnabled = values?.['briefing']?.[getId]?.enabled
  const onSwitchChange = (value) => {
    const enableSwitch = value.length > 0

    const nextBriefing = {
      ...(values?.briefing || {}),
      [getId]: {
        ...(values?.briefing?.[getId] || {}),
        enabled: enableSwitch
      }
    }

    if (enableSwitch) {
      // onRowClick?.(getId)
      // setExpandedLabel?.(getId)
    }

    setValues(nextBriefing)
  }

  return (
    <VuiFormGroup
      fieldProps={{
        color,
        items: [
          {
            id: `${getId}-enableAiWorker`,
            label: row.switchLabel,
            value: 'enabled'
          }
        ],
        onChange: onSwitchChange,
        value: getEnabled ? ['enabled'] : []
      }}
      fieldType='toggleinput'
      groupLayout='block'
      hideLabel
      id={`${getId}-aiWorkerForm`}
    />
  )
}

export const generateInsightChartPayload = ({ insightsData, chartTitle, dashboardId, isEditMode = false, chartId }) => {
  const safeArray = (v) => (Array.isArray(v) ? v : [])
  const optValue = (v) => (v && typeof v === 'object' ? v.value : v)
  const toEnum = (v) => {
    const val = optValue(v)
    return val === undefined || val === null || val === '' ? undefined : new EnumType(val)
  }
  const toEnumList = (arr) => safeArray(arr).map(toEnum).filter(Boolean)
  const firstEnum = (arr) => {
    const first = safeArray(arr)[0]
    return toEnum(first)
  }
  const toBoolean = (v, fallback = false) => {
    if (Array.isArray(v)) return !!v[0]
    if (typeof v === 'boolean') return v
    return fallback
  }

  const modeValue = optValue(safeArray(insightsData?.mode)[0])
  const dataSourceValue = optValue(safeArray(insightsData?.dataSource)[0])
  const selectedCharts = safeArray(insightsData?.chartsToInclude)
    .map((item) => optValue(item))
    .filter(Boolean)

  const payload = {
    dataSource: new EnumType(dataSourceValue),
    title: chartTitle,
    chartsToInclude: selectedCharts,
    dashboardId,
    mode: new EnumType(modeValue),
  }

  if (isEditMode && chartId) {
    payload.id = chartId
  }

  if (modeValue === 'briefing') {
    const briefingData = insightsData?.briefing || {}

    payload.briefing = {
      enabled: true,
      title: briefingData.title || '',
      highlightKeyItems: toBoolean(briefingData.highlightKeyItems, true),
      summary: {
        enabled: !!briefingData?.summary?.enabled,
        subtitle: briefingData?.summary?.subtitle || '',
        instructions: briefingData?.summary?.instructions || '',
        include: toEnumList(briefingData?.summary?.include),
        format: firstEnum(briefingData?.summary?.format)
      },
      changeDrivers: {
        enabled: !!briefingData?.changeDrivers?.enabled,
        subtitle: briefingData?.changeDrivers?.subtitle || '',
        instructions: briefingData?.changeDrivers?.instructions || '',
        focusOn: toEnum(briefingData?.changeDrivers?.focusOn?.[0]),
        include: toEnumList(briefingData?.changeDrivers?.include),
        format: firstEnum(briefingData?.changeDrivers?.format),
        changeSensitivity: firstEnum(briefingData?.changeDrivers?.changeSensitivity)
      },
      anomalyRadar: {
        enabled: !!briefingData?.anomalyRadar?.enabled,
        subtitle: briefingData?.anomalyRadar?.subtitle || '',
        exclude: toEnumList(briefingData?.anomalyRadar?.exclude),
        instructions: briefingData?.anomalyRadar?.instructions || '',
        include: toEnumList(briefingData?.anomalyRadar?.include),
        format: firstEnum(briefingData?.anomalyRadar?.format),
        changeSensitivity: firstEnum(briefingData?.anomalyRadar?.changeSensitivity)
      },
      patternFinder: {
        enabled: !!briefingData?.patternFinder?.enabled,
        subtitle: briefingData?.patternFinder?.subtitle || '',
        instructions: briefingData?.patternFinder?.instructions || '',
        include: toEnumList(briefingData?.patternFinder?.include),
        format: firstEnum(briefingData?.patternFinder?.format),
        changeSensitivity: firstEnum(briefingData?.patternFinder?.changeSensitivity)
      },
      timelineOfEvents: {
        enabled: !!briefingData?.timelineOfEvents?.enabled,
        subtitle: briefingData?.timelineOfEvents?.subtitle || '',
        instructions: briefingData?.timelineOfEvents?.instructions || '',
        include: toEnumList(briefingData?.timelineOfEvents?.include),
        format: firstEnum(briefingData?.timelineOfEvents?.format),
      },
      proposeActions: {
        enabled: !!briefingData?.proposeActions?.enabled,
        subtitle: briefingData?.proposeActions?.subtitle || '',
        instructions: briefingData?.proposeActions?.instructions || '',
        actionType: toEnumList(briefingData?.proposeActions?.actionType),
        format: firstEnum(briefingData?.proposeActions?.format),
      }
    }
  }

  if (modeValue === 'keyFinding') {
    const keyFindingData = insightsData?.keyFinding || {}
    payload.keyFinding = {
      enabled: true,
      instructions: keyFindingData.instructions || '',
      include: toEnumList(keyFindingData.include) || [],
      textSize: keyFindingData?.textSize?.[0]?.value || '',
      highlightKeyItems: toBoolean(keyFindingData.highlightKeyItems, false)
    }
  }

  return payload
}